<template>
  <div class="purchase-history">
    <div class="card shadow-sm">
      <div class="card-body">
        <h2 v-if="showTitle" class="card-title mb-3">
          <font-awesome-icon icon="history" class="me-2" />
          Kaufhistorie
          <small class="text-muted">({{ combinedBillingHistory.length }} Einträge)</small>
        </h2>
        
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Laden...</span>
          </div>
          <p class="mt-2 text-muted">Lade Kaufhistorie...</p>
        </div>
        
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Nummer</th>
                  <th>Typ</th>
                  <th>Details</th>
                  <th>Betrag</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="combinedBillingHistory.length === 0">
                  <td colspan="7" class="text-center py-3">Keine Käufe vorhanden</td>
                </tr>
                <tr v-for="billing in combinedBillingHistory" :key="`${billing.type}-${billing.id}`">
                  <td>{{ formatDate(billing.createdAt) }}</td>
                  <td v-if="billing.method !== 'in_app_purchase'">
                    <div v-if="billing.invoiceNumber || billing.reminderInvoiceNumber">
                      <div class="fw-semibold">
                        {{ billing.invoiceNumber || billing.reminderInvoiceNumber }}
                      </div>
                      <div class="text-muted small">
                        {{ getDocumentLabel(billing) }}
                      </div>
                    </div>
                    <span v-else-if="billing.method === 'coupon'" class="text-muted">
                      Coupon-Kauf
                    </span>
                    <span v-else-if="billing.method === 'free'" class="text-muted">
                      Kostenlos
                    </span>
                    <span v-else class="text-muted">
                      Wird generiert...
                    </span>
                  </td>
                  <td v-else>{{ billing.paymentDetails.platform === 'google' ? 'Google' : 'Apple' }} In-App-Kauf </td>
                  <td>
                    <span :class="getTypeBadgeClass(billing.type)" class="badge">
                      {{ getTypeDisplayName(billing.type) }}
                    </span>
                  </td>
                  <td>
                    <div>
                      <div class="fw-semibold">{{ billing.displayName }}</div>
                      <div class="text-muted small">{{ billing.customerName || (user ? `${user.firstName} ${user.lastName}` : 'Unbekannt') }}</div>
                    </div>
                  </td>
                  <td>{{ formatCurrency(billing.price || billing.amount) }}</td>
                  <td>
                    <span :class="getStatusClass(billing.status)">
                      {{ getStatusText(billing.status) }}
                    </span>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-primary"
                        @click="downloadInvoice(billing)"
                        :disabled="!canDownloadInvoice(billing)"
                        title="PDF herunterladen"
                        v-if="billing.method !== 'in_app_purchase'"
                      >
                        <font-awesome-icon icon="download" />
                      </button>
                      
                      <!-- PayPal Buttons für ausstehende Zahlungen -->
                      <button 
                        v-if="billing.type === 'coin' && billing.status === 'pending' && billing.method === 'paypal'"
                        class="btn btn-outline-success"
                        @click="recheckPayPalPayment(billing.id)"
                        title="PayPal-Zahlung überprüfen"
                      >
                        <font-awesome-icon icon="sync-alt" />
                      </button>
                      
                      <button 
                        v-if="billing.type === 'coin' && billing.status === 'pending' && billing.method === 'paypal'"
                        class="btn btn-outline-warning"
                        @click="goToPayPal(billing.id)"
                        title="Zu PayPal gehen"
                      >
                        <font-awesome-icon icon="external-link-alt" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <nav v-if="pagination && pagination.totalPages > 1" aria-label="Paginierung">
            <ul class="pagination justify-content-center">
              <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
                <button class="page-link" @click="loadBillingHistory(pagination.page - 1)" :disabled="pagination.page <= 1">
                  Zurück
                </button>
              </li>
              <li 
                v-for="page in visiblePages" 
                :key="page" 
                class="page-item" 
                :class="{ active: page === pagination.page }"
              >
                <button class="page-link" @click="loadBillingHistory(page)">
                  {{ page }}
                </button>
              </li>
              <li class="page-item" :class="{ disabled: pagination.page >= pagination.totalPages }">
                <button class="page-link" @click="loadBillingHistory(pagination.page + 1)" :disabled="pagination.page >= pagination.totalPages">
                  Weiter
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { showError, showSuccess } from '@/services/notification'
import { storeToRefs } from 'pinia'
import { 
  getBillingHistory as getBillingHistoryApi,
  downloadInvoice as downloadInvoiceApi,
  recheckPayPalPayment as recheckPayPalPaymentApi,
  getPayPalRedirectUrl as getPayPalRedirectUrlApi
} from '@/api/billing'
import { teamBillingApi } from '@/api/teamBilling'


const props = defineProps({
  limit: {
    type: Number,
    default: 10
  },
  showTitle: {
    type: Boolean,
    default: true
  }
})

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)


const loading = ref(false)
const billingHistory = ref([])
const teamBillingHistory = ref([])
const combinedBillingHistory = ref([])
const pagination = ref({
  total: 0,
  page: 1,
  limit: props.limit,
  totalPages: 1
})


const visiblePages = computed(() => {
  if (!pagination.value) return []
  
  const current = pagination.value.page
  const total = pagination.value.totalPages
  const delta = 2
  const range = []
  
  for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
    range.push(i)
  }
  
  return range
})


const loadBillingHistory = async (page = 1) => {
  try {
    loading.value = true
    
    
    
    let coinResponse = { billings: [] }
    let teamResponse = { teamBillings: [] }
    
    
    try {
      coinResponse = await getBillingHistoryApi(1, 1000) 
    } catch (coinError) {
      console.error('Error loading coin billings:', coinError)
      showError('Quittungs-Käufe konnten nicht geladen werden')
    }
    
    
    try {
      teamResponse = await teamBillingApi.getUserTeamBillings(1, 1000) 
    } catch (teamError) {
      console.error('Error loading team billings:', teamError)
    }
    
    billingHistory.value = coinResponse.billings || []
    teamBillingHistory.value = teamResponse.teamBillings || []

    
    
    const combined = [
      ...billingHistory.value.map(billing => ({
        ...billing,
        type: 'coin',
        displayName: `${billing.coins} Quittungen`,
        customerName: billing.User ? `${billing.User.firstName} ${billing.User.lastName}` : billing.firstName ? `${billing.firstName} ${billing.lastName}` : `${user.value.firstName} ${user.value.lastName}`,
        customerEmail: billing.User ? billing.User.email : billing.email
      })),
      ...teamBillingHistory.value.map(billing => ({
        ...billing,
        type: 'team',
        displayName: `${billing.plan === 'pro' ? 'Pro' : 'Ultimate'} Plan`,
        customerName: billing.team ? billing.team.name : 'Team',
        customerEmail: billing.email,
        coins: null 
      }))
    ]
    
    combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    
    
    const startIndex = (page - 1) * pagination.value.limit
    const endIndex = startIndex + pagination.value.limit
    const paginatedItems = combined.slice(startIndex, endIndex)
    
    combinedBillingHistory.value = paginatedItems
    
    
    
    const totalItems = combined.length
    const totalPages = Math.ceil(totalItems / pagination.value.limit)
    
    pagination.value = {
      total: totalItems,
      page: page,
      limit: pagination.value.limit,
      totalPages: totalPages
    }
    
  } catch (error) {
    console.error('Error loading billing history:', error)
    showError('Fehler beim Laden der Kaufhistorie: ' + (error.response?.data?.message || error.message))
    
    
    billingHistory.value = []
    teamBillingHistory.value = []
    combinedBillingHistory.value = []
    pagination.value = {
      total: 0,
      page: 1,
      limit: props.limit,
      totalPages: 1
    }
  } finally {
    loading.value = false
  }
}

const getDocumentLabel = (billing) => {
  if (billing.method === 'bank_transfer' && !billing.payed) {
    return 'Zahlungsaufforderung'
  }
  return 'Rechnung'
}

const downloadInvoice = async (billing) => {
  try {
    let response
    if (billing.type === 'team') {
      response = await teamBillingApi.downloadTeamBillingInvoice(billing.id)
    } else {
      response = await downloadInvoiceApi(billing.id)
    }
    
    
    const contentDisposition = response.headers?.['content-disposition']
    const documentLabel = getDocumentLabel(billing)
    const safeLabel = documentLabel.replace(/\s+/g, '_')
    let filename = `${safeLabel}_${billing.invoiceNumber || billing.reminderInvoiceNumber || billing.id}.pdf` 
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }
    
    const url = window.URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    
    showSuccess(`${documentLabel} wird heruntergeladen...`)
  } catch (error) {
    console.error('Download error:', error)
    showError('Fehler beim Herunterladen des Dokuments')
  }
}

const recheckPayPalPayment = async (billingId) => {
  try {
    const result = await recheckPayPalPaymentApi(billingId)
    
    if (result.status === 'completed') {
      showSuccess('Zahlung erfolgreich bestätigt!')
      await loadBillingHistory(pagination.value.page)
    } else if (result.status === 'pending') {
      showError('Zahlung ist noch ausstehend. Bitte versuch es später erneut oder geh zu PayPal.')
    } else {
      showError('Zahlung fehlgeschlagen. Bitte kontaktiere den Support.')
    }
  } catch (error) {
    console.error('PayPal recheck error:', error)
    showError(error.response?.data?.message || 'Fehler beim Überprüfen der PayPal-Zahlung')
  }
}

const goToPayPal = async (billingId) => {
  try {
    const result = await getPayPalRedirectUrlApi(billingId)
    window.location.href = result.redirectUrl
  } catch (error) {
    console.error('PayPal redirect error:', error)
    showError(error.response?.data?.message || 'Fehler beim Weiterleiten zu PayPal')
  }
}


const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE')
}

const canDownloadInvoice = (billing) => {
  // Kann heruntergeladen werden wenn:
  // 1. Status ist 'completed' (bezahlt)
  // 2. Status ist 'pending' und Methode ist 'bank_transfer' und reminderInvoiceNumber existiert
  // 3. Status ist 'pending' und Methode ist 'free' oder 'coupon' (kostenlose Rechnungen)
  return billing.status === 'completed' || 
         (billing.status === 'pending' && billing.method === 'bank_transfer' && billing.reminderInvoiceNumber) ||
         (billing.status === 'pending' && (billing.method === 'free' || billing.method === 'coupon'))
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
}

const getStatusClass = (status) => {
  switch (status) {
    case 'pending':
      return 'badge bg-warning'
    case 'completed':
      return 'badge bg-success'
    case 'failed':
      return 'badge bg-danger'
    default:
      return 'badge bg-secondary'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'pending':
      return 'Ausstehend'
    case 'completed':
      return 'Abgeschlossen'
    case 'failed':
      return 'Fehlgeschlagen'
    default:
      return status
  }
}

const getTypeDisplayName = (type) => {
  switch (type) {
    case 'coin': return 'Quittung-Kauf'
    case 'team': return 'Team-Upgrade'
    default: return type
  }
}

const getTypeBadgeClass = (type) => {
  switch (type) {
    case 'coin': return 'badge-coin'
    case 'team': return 'badge-team'
    default: return 'badge-secondary'
  }
}


defineExpose({
  loadBillingHistory,
  combinedBillingHistory
})


onMounted(async () => {
  try {

    await loadBillingHistory()
  } catch (error) {
    console.error('PurchaseHistory: Error in onMounted:', error)
  }
})
</script>

<style scoped>
.badge-coin {
  background-color: #ffc107;
  color: #000;
}

.badge-team {
  background-color: #198754;
  color: #fff;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.purchase-history .table th {
  border-top: none;
  font-weight: 600;
  color: var(--text-primary);
}

.purchase-history .table-hover tbody tr:hover {
  background-color: var(--bg-hover, rgba(0, 0, 0, 0.075));
}
</style>
